import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../Context/AppProvider';
import { toast } from 'react-toastify';
import { 
  FaEye, 
  FaEyeSlash, 
  FaShieldAlt, 
  FaLock, 
  FaUserShield, 
  FaSpinner,
  FaExclamationTriangle,
  FaClock,
  FaKey
} from 'react-icons/fa';

const AdminLogin = () => {
  const { loginAdmin, isAdminLoggedIn } = useAppContext();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({ email: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [lockoutTime, setLockoutTime] = useState(null);
  const [securityLevel, setSecurityLevel] = useState('medium');

  useEffect(() => {
    if (isAdminLoggedIn) {
      navigate('/admin', { replace: true });
    }
  }, [isAdminLoggedIn, navigate]);

  // Check for existing lockout
  useEffect(() => {
    const lockoutEnd = localStorage.getItem('adminLockoutEnd');
    if (lockoutEnd && new Date() < new Date(lockoutEnd)) {
      setIsLocked(true);
      setLockoutTime(new Date(lockoutEnd));
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    
    // Password strength indicator
    if (name === 'password') {
      if (value.length >= 12 && /[A-Z]/.test(value) && /[0-9]/.test(value) && /[^A-Za-z0-9]/.test(value)) {
        setSecurityLevel('high');
      } else if (value.length >= 8 && /[A-Z]/.test(value) && /[0-9]/.test(value)) {
        setSecurityLevel('medium');
      } else if (value.length >= 6) {
        setSecurityLevel('low');
      } else {
        setSecurityLevel('very-low');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isLocked) {
      toast.error('Account temporarily locked due to multiple failed attempts.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address.');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters long.');
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/admin/login`,
        formData,
        {
          timeout: 10000, // 10 second timeout
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          }
        }
      );

      const token = response.data?.responseData?.token;

      if (token) {
        // Reset login attempts on successful login
        setLoginAttempts(0);
        localStorage.removeItem('adminLockoutEnd');
        loginAdmin(token);
        toast.success('Login successful! Redirecting to dashboard...');
        navigate('/admin', { replace: true });
      } else {
        toast.error('Login successful, but no token received.');
        console.error('No token in response:', response.data);
      }
    } catch (err) {
      const newAttempts = loginAttempts + 1;
      setLoginAttempts(newAttempts);
      
      // Lock account after 5 failed attempts
      if (newAttempts >= 5) {
        const lockoutEnd = new Date(Date.now() + 15 * 60 * 1000); // 15 minutes
        setLockoutTime(lockoutEnd);
        setIsLocked(true);
        localStorage.setItem('adminLockoutEnd', lockoutEnd.toISOString());
        toast.error('Account locked for 15 minutes due to multiple failed attempts.');
        return;
      }
      
      const message = err.response?.data?.message || 'Login failed. Check credentials.';
      toast.error(`${message} (Attempt ${newAttempts}/5)`);
    } finally {
      setIsLoading(false);
    }
  };

  const getSecurityColor = () => {
    switch (securityLevel) {
      case 'high': return 'text-green-500';
      case 'medium': return 'text-yellow-500';
      case 'low': return 'text-orange-500';
      default: return 'text-red-500';
    }
  };

  const getSecurityText = () => {
    switch (securityLevel) {
      case 'high': return 'Strong';
      case 'medium': return 'Medium';
      case 'low': return 'Weak';
      default: return 'Very Weak';
    }
  };

  if (isAdminLoggedIn) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 flex items-center justify-center p-4 relative">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          backgroundRepeat: 'repeat'
        }}></div>
      </div>
      
      <div className="relative w-full max-w-md">
        {/* Security Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full mb-4 shadow-lg">
            <FaShieldAlt className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">SmartStart AI</h1>
          <h2 className="text-xl text-blue-200 mb-4">Admin Portal</h2>
          <div className="flex items-center justify-center space-x-2 text-sm text-blue-300">
            <FaUserShield className="w-4 h-4" />
            <span>Secure Administrator Access</span>
          </div>
        </div>

        {/* Main Login Card */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl shadow-2xl border border-white/20 p-8">
          {/* Security Status */}
          <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
            <div className="flex items-center space-x-2 text-yellow-300 text-sm">
              <FaExclamationTriangle className="w-4 h-4" />
              <span>Secure connection required. All activities are logged and monitored.</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-white mb-2">
                Administrator Email
              </label>
              <div className="relative">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  disabled={isLoading || isLocked}
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  placeholder="admin@smartstartai.com"
                  aria-label="Email"
                />
                <FaUserShield className="absolute right-3 top-3.5 w-4 h-4 text-gray-400" />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-white mb-2">
                Secure Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  disabled={isLoading || isLocked}
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 pr-12"
                  placeholder="Enter your secure password"
                  aria-label="Password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-3.5 text-gray-400 hover:text-white transition-colors"
                  tabIndex={-1}
                  aria-label="Toggle password visibility"
                >
                  {showPassword ? <FaEyeSlash className="w-4 h-4" /> : <FaEye className="w-4 h-4" />}
                </button>
              </div>
              
              {/* Password Strength Indicator */}
              {formData.password && (
                <div className="mt-2 flex items-center space-x-2">
                  <span className="text-xs text-gray-300">Security Level:</span>
                  <span className={`text-xs font-medium ${getSecurityColor()}`}>
                    {getSecurityText()}
                  </span>
                  <div className="flex space-x-1">
                    {[1, 2, 3, 4].map((level) => (
                      <div
                        key={level}
                        className={`w-2 h-2 rounded-full ${
                          level <= (securityLevel === 'high' ? 4 : securityLevel === 'medium' ? 3 : securityLevel === 'low' ? 2 : 1)
                            ? getSecurityColor().replace('text-', 'bg-')
                            : 'bg-gray-600'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Account Lockout Warning */}
            {isLocked && (
              <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                <div className="flex items-center space-x-2 text-red-300 text-sm">
                  <FaLock className="w-4 h-4" />
                  <span>Account temporarily locked. Try again in {Math.ceil((lockoutTime - new Date()) / 60000)} minutes.</span>
                </div>
              </div>
            )}

            {/* Login Attempts Warning */}
            {loginAttempts > 0 && !isLocked && (
              <div className="p-3 bg-orange-500/10 border border-orange-500/20 rounded-lg">
                <div className="flex items-center space-x-2 text-orange-300 text-sm">
                  <FaExclamationTriangle className="w-4 h-4" />
                  <span>Failed attempts: {loginAttempts}/5</span>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading || isLocked}
              className={`w-full py-3 px-4 rounded-lg font-semibold text-white transition-all duration-200 flex items-center justify-center space-x-2 ${
                isLoading || isLocked
                  ? 'bg-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-transparent shadow-lg hover:shadow-xl'
              }`}
              aria-label="Login Button"
            >
              {isLoading ? (
                <>
                  <FaSpinner className="w-4 h-4 animate-spin" />
                  <span>Authenticating...</span>
                </>
              ) : isLocked ? (
                <>
                  <FaLock className="w-4 h-4" />
                  <span>Account Locked</span>
                </>
              ) : (
                <>
                  <FaShieldAlt className="w-4 h-4" />
                  <span>Secure Login</span>
                </>
              )}
            </button>
          </form>

          {/* Security Footer */}
          <div className="mt-8 pt-6 border-t border-white/10">
            <div className="text-center text-xs text-gray-300 space-y-2">
              <div className="flex items-center justify-center space-x-4">
                <div className="flex items-center space-x-1">
                  <FaShieldAlt className="w-3 h-3" />
                  <span>SSL Encrypted</span>
                </div>
                <div className="flex items-center space-x-1">
                  <FaClock className="w-3 h-3" />
                  <span>Session Timeout: 30min</span>
                </div>
              </div>
              <p className="text-gray-400">
                All login attempts are monitored and logged for security purposes.
              </p>
            </div>
          </div>
        </div>

        {/* Additional Security Info */}
        <div className="mt-6 text-center text-sm text-blue-200">
          <p>© 2025 SmartStart AI. All rights reserved.</p>
          <p className="mt-1">Powered by advanced AI technology</p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
